import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './ReportGeneration.css';
import LearningGoalsModal from './LearningGoalsModal';
import CourseAlignmentOverview from './CourseAlignmentOverview';
import Recommendations from './Recommendations';
import { getAllTemplates, getAlignmentScore, exportExcelReport, getEvaluateResult, startAssessmentByTemplate, getSurveyAnalyses, generatePdfReport } from '../services/api';
import IndirectMethodSummary from './IndirectMethodSummary';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';

import { useSelectedTemplate } from '../context/SelectedTemplateContext';
import { useLocation } from 'react-router-dom';
import { useNotification, NotificationContainer } from './Notification';
import { Icon } from '@iconify/react';

const ReportGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTemplate, setSelectedTemplate } = useSelectedTemplate();
  
  // Add debugging for template
  console.log('ReportGeneration - selectedTemplate:', selectedTemplate);
  console.log('ReportGeneration - selectedTemplate?.id:', selectedTemplate?.id);
  
  // Get program data from navigation state
  const programData = location.state?.programData || {
    name: location.state?.programName || 'Program Report',
    category: 'Undergraduate',
    description: ''
  };

  // Add state to track template loading attempts (moved before useEffect)
  const [templateLoadAttempted, setTemplateLoadAttempted] = useState(false);
  
  // Add state to track if a report has been generated
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  
  // Add ref to store abort controller for cancellation
  const abortControllerRef = useRef(null);
  
  // Add state for survey selection
  const [availableSurveys, setAvailableSurveys] = useState([]);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  
  // Load template from localStorage if not already loaded
  useEffect(() => {
    console.log('ReportGeneration - useEffect triggered');
    console.log('ReportGeneration - selectedTemplate:', selectedTemplate);
    console.log('ReportGeneration - programData:', programData);
    console.log('ReportGeneration - templateLoadAttempted:', templateLoadAttempted);
    
    // If we already have a template, check if it's complete
    if (selectedTemplate) {
      console.log('ReportGeneration - Template already available in context:', selectedTemplate);
      // Check if the template has the required LG array (complete template)
      if (selectedTemplate.LG && Array.isArray(selectedTemplate.LG)) {
        console.log('ReportGeneration - Template is complete, using existing template');
        return;
      } else {
        console.log('ReportGeneration - Template is incomplete, will try to load complete version');
      }
    }
    
    // If we've already attempted to load, don't try again
    if (templateLoadAttempted) {
      console.log('ReportGeneration - Template load already attempted, skipping');
      return;
    }
    
    setTemplateLoadAttempted(true);
    
    // Try to load complete template from localStorage first
    const templateStorageKey = `selected_template_${programData.name?.replace(/\s+/g, '_') || 'default'}_${new URLSearchParams(window.location.search).get('school') || 'MARYLAND'}`;
    const savedCompleteTemplate = localStorage.getItem(templateStorageKey);
    
    if (savedCompleteTemplate) {
      try {
        const parsedTemplate = JSON.parse(savedCompleteTemplate);
        if (parsedTemplate.LG && Array.isArray(parsedTemplate.LG)) {
          console.log('ReportGeneration - Found complete template in localStorage:', parsedTemplate);
          setSelectedTemplate(parsedTemplate);
          return;
        }
      } catch (err) {
        console.error('ReportGeneration - Error parsing complete template from localStorage:', err);
      }
    }
    
    // Fallback: read template_id directly from localStorage
    const storedTemplateId = localStorage.getItem('report_generation_template_id');
    console.log('ReportGeneration - Stored template_id from localStorage:', storedTemplateId);
    
    if (storedTemplateId) {
      // Create a simple template object with the ID
      const templateFromStorage = {
        id: parseInt(storedTemplateId),
        name: 'Template from localStorage'
      };
      console.log('ReportGeneration - Created template from stored ID:', templateFromStorage);
      setSelectedTemplate(templateFromStorage);
      return;
    }
    
    console.log('ReportGeneration - No template_id found in localStorage, will use fallback ID');
  }, [selectedTemplate, setSelectedTemplate, templateLoadAttempted, programData.name]);
  
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Organize, 3: Review & Download
  const [isDragOver, setIsDragOver] = useState(false);
  const [programInfoFiles, setProgramInfoFiles] = useState([]);
  const [fileSections, setFileSections] = useState({});
  const [pageRangeErrors, setPageRangeErrors] = useState({});
  const [filePanelStates, setFilePanelStates] = useState({});
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState('3 min 0 s');
  const [progressInterval, setProgressInterval] = useState(null);
  const [progressStartTime, setProgressStartTime] = useState(null);
  const [generatedReportFiles, setGeneratedReportFiles] = useState({});
  const [selectedFileType, setSelectedFileType] = useState('PDF');
  const [pdfPreviewError, setPdfPreviewError] = useState(false);
  const [pdfConversionError, setPdfConversionError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalUploadSize, setTotalUploadSize] = useState(0);
  const [pdfPreviews, setPdfPreviews] = useState({});
  
  // Add sectionPanelStates state
  const [sectionPanelStates, setSectionPanelStates] = useState({});

  // Log initial state on component mount
  useEffect(() => {
    console.log('ReportGeneration - Component mounted');
    console.log('ReportGeneration - Initial selectedTemplate:', selectedTemplate);
    console.log('ReportGeneration - Initial programData:', programData);
    console.log('ReportGeneration - Initial location.state:', location.state);
  }, []);
  
  // Toggle states for specific sections only
  const [aiEvaluationToggles, setAiEvaluationToggles] = useState({
    'Student Surveys': false,
    'Learning Outcomes Assessment': false
  });

  // Available sections for program documents
  const programSections = [
    'External Reviewer Letter',
    'Program Description', 
    'Program Curriculum',
    'Program Evaluation',
    'Student Surveys',
    'Learning Outcomes Assessment',
    'School Online Resources'
  ];

  // Progression steps configuration
  const progressionSteps = [
    { number: 1, title: 'Upload Documents', active: currentStep === 1, completed: currentStep > 1 },
    { number: 2, title: 'Organize Files By Section', active: currentStep === 2, completed: currentStep > 2 },
    { number: 3, title: 'Review & Download', active: currentStep === 3, completed: false }
  ];

  // Update the max files constant
  const MAX_FILES = 10;

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      if (generatedReportFiles.word?.blobUrl) {
        window.URL.revokeObjectURL(generatedReportFiles.word.blobUrl);
      }
      if (generatedReportFiles.pdf?.blobUrl) {
        window.URL.revokeObjectURL(generatedReportFiles.pdf.blobUrl);
      }
    };
  }, [generatedReportFiles]);

  // Cleanup progress interval on component unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      // Cleanup abort controller on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [progressInterval]);

  // Reset PDF preview error when file type changes
  useEffect(() => {
    setPdfPreviewError(false);
  }, [selectedFileType]);

  // Initialize total upload size when component loads
  useEffect(() => {
    if (programInfoFiles.length > 0) {
      const totalSize = programInfoFiles.reduce((acc, file) => acc + file.size, 0);
      setTotalUploadSize(totalSize);
    }
  }, []); // Only run once on component mount

  // Fetch available surveys for the current school
  const fetchAvailableSurveys = useCallback(async () => {
    try {
      setSurveysLoading(true);
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      
      const surveyData = await getSurveyAnalyses(school);
      
      // Extract survey names from the response
      const surveyNames = (surveyData || [])
        .filter(survey => survey.status === 'completed')
        .map(survey => survey.original_filename)
        .filter(name => name); // Filter out any null/undefined names
      
      setAvailableSurveys(surveyNames);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setAvailableSurveys([]);
    } finally {
      setSurveysLoading(false);
    }
  }, []);

  // Fetch surveys on component mount
  useEffect(() => {
    fetchAvailableSurveys();
  }, [fetchAvailableSurveys]);

  // Handle survey selection from dropdown
  const handleSurveySelect = (surveyName) => {
    if (surveyName && !selectedSurveys.includes(surveyName)) {
      setSelectedSurveys(prev => [...prev, surveyName]);
    }
  };

  // Handle removing a selected survey
  const handleRemoveSurvey = (surveyName) => {
    setSelectedSurveys(prev => prev.filter(name => name !== surveyName));
  };

  // Generate PDF preview thumbnail
  const generatePdfPreview = async (file) => {
    try {
      // Set up PDF.js worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      
      // Create a URL for the file
      const fileUrl = URL.createObjectURL(file);
      
      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument(fileUrl);
      const pdf = await loadingTask.promise;
      
      // Get the first page
      const page = await pdf.getPage(1);
      
      // Set up canvas for rendering
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Set canvas size for thumbnail
      const viewport = page.getViewport({ scale: 1.0 });
      
      // Calculate scale to fit the page completely within the preview area
      const maxWidth = 200; // Approximate card width
      const maxHeight = 160; // Match CSS .pdf-preview-image height
      const scaleX = maxWidth / viewport.width;
      const scaleY = maxHeight / viewport.height;
      const scale = Math.min(scaleX, scaleY, 1.0); // Don't scale up, only down
      
      const scaledViewport = page.getViewport({ scale });
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      
      // Render the page
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport
      };
      
      await page.render(renderContext).promise;
      
      // Convert canvas to data URL
      const previewUrl = canvas.toDataURL('image/png');
      
      // Clean up
      URL.revokeObjectURL(fileUrl);
      
      return previewUrl;
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      // Return null to show placeholder
      return null;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // If we're going back from step 3 (generated report), mark that we have a generated report
      if (currentStep === 3) {
        setHasGeneratedReport(true);
      }
      setCurrentStep(currentStep - 1);
    } else {
      // Get school from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      
      // Preserve application state for the AssuranceReport
      const preservedState = {
        ...location.state,
        returnedFromReportGeneration: true,
        selectedTemplate: selectedTemplate,
        programData: programData,
        templateId: selectedTemplate?.id,
        courseList: location.state?.courseList || [], // Preserve courseList
        // Preserve any other relevant state
        tab: 'Curriculum Mapping' // Default tab to return to
      };
      
      // Also store state in sessionStorage as backup
      const backupState = {
        selectedTemplate: selectedTemplate,
        programData: programData,
        courseList: location.state?.courseList || [], // Preserve courseList in backup
        returnedFromReportGeneration: true,
        timestamp: Date.now()
      };
      sessionStorage.setItem('report_generation_return_state', JSON.stringify(backupState));
      
      // Navigate back to the AssuranceReport or Evidence Library
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: preservedState
        });
      } else {
        navigate(`/assessment?school=${school}`, {
          state: preservedState
        });
      }
    }
  };

  // Program info file handling
  const handleProgramInfoFileInput = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      processProgramInfoFiles(files);
    }
  };

  const handleProgramInfoDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleProgramInfoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleProgramInfoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleProgramInfoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processProgramInfoFiles(files);
    }
  };

  const processProgramInfoFiles = (files) => {
    const fileArray = Array.from(files);
    // Filter for PDF files only and limit to 10 files total
    const pdfFiles = fileArray.filter(file => file.type === 'application/pdf');
    const remainingSlots = MAX_FILES - programInfoFiles.length;
    const filesToAdd = pdfFiles.slice(0, remainingSlots);
    
    if (pdfFiles.length > filesToAdd.length) {
      alert(`Only ${remainingSlots} more files can be added. Maximum ${MAX_FILES} files allowed.`);
      return;
    }
    
    if (fileArray.length > pdfFiles.length) {
      alert('Only PDF files are supported.');
      return;
    }
    
    // Initialize sections and panel states for new files
    const newFileSections = {};
    const newPanelStates = {};
    
    filesToAdd.forEach(file => {
      newFileSections[file.name] = {
        sections: {},
        pageRanges: {}
      };
      newPanelStates[file.name] = 'edit';
    });
    
    setProgramInfoFiles(prev => {
      const newFiles = [...prev, ...filesToAdd];
      // Calculate total size of all files
      const totalSize = newFiles.reduce((acc, file) => acc + file.size, 0);
      setTotalUploadSize(totalSize);
      return newFiles;
    });
    
    setFileSections(prev => ({ ...prev, ...newFileSections }));
    setFilePanelStates(prev => ({ ...prev, ...newPanelStates }));
    
    // Generate previews for new files
    filesToAdd.forEach(async (file) => {
      const previewUrl = await generatePdfPreview(file);
      if (previewUrl) {
        setPdfPreviews(prev => ({
          ...prev,
          [file.name]: previewUrl
        }));
      }
    });
    
    // Simulate upload progress
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleProgramInfoUploadAreaClick = () => {
    document.getElementById('program-info-file-input').click();
  };

  // Handle AI evaluation toggle for specific sections
  const handleAiEvaluationToggle = (sectionName) => {
    setAiEvaluationToggles(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Handle section checkbox changes
  const handleSectionChange = (fileName, section, checked) => {
    setFileSections(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        sections: {
          ...prev[fileName].sections,
          [section]: checked
        }
      }
    }));
    
    // Clear error when section is unchecked
    if (!checked) {
      setPageRangeErrors(prev => ({
        ...prev,
        [`${fileName}-${section}`]: false
      }));
    }
  };

  // Handle page range changes
  const handlePageRangeChange = (fileName, section, pageRange) => {
    setFileSections(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        pageRanges: {
          ...prev[fileName].pageRanges,
          [section]: pageRange
        }
      }
    }));
    
    // Clear error when user starts typing
    if (pageRange.trim()) {
      setPageRangeErrors(prev => ({
        ...prev,
        [`${fileName}-${section}`]: false
      }));
    }
  };

  // Handle page range input focus
  const handlePageRangeFocus = (fileName, section) => {
    // Clear error when user focuses on the input
    setPageRangeErrors(prev => ({
      ...prev,
      [`${fileName}-${section}`]: false
    }));
  };

  // Handle page range input blur (when user leaves the input)
  const handlePageRangeBlur = (fileName, section) => {
    const pageRange = fileSections[fileName]?.pageRanges[section] || '';
    const isChecked = fileSections[fileName]?.sections[section] || false;
    
    // Show error if section is checked but page range is empty
    if (isChecked && !pageRange.trim()) {
      setPageRangeErrors(prev => ({
        ...prev,
        [`${fileName}-${section}`]: true
      }));
    }
  };

  // Reset sections for a file
  const handleResetSections = (fileName) => {
    setFileSections(prev => ({
      ...prev,
      [fileName]: {
        sections: {},
        pageRanges: {}
      }
    }));
  };

  // Remove a file
  const handleRemoveFile = (fileName) => {
    setProgramInfoFiles(prev => {
      const newFiles = prev.filter(file => file.name !== fileName);
      // Recalculate total size after removing file
      const totalSize = newFiles.reduce((acc, file) => acc + file.size, 0);
      setTotalUploadSize(totalSize);
      return newFiles;
    });
    
    setFileSections(prev => {
      const newSections = { ...prev };
      delete newSections[fileName];
      return newSections;
    });
    
    // Clear preview for removed file
    setPdfPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[fileName];
      return newPreviews;
    });
    
    // Clear all errors for this file
    setPageRangeErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${fileName}-`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    // Remove panel state for this file
    setFilePanelStates(prev => {
      const newStates = { ...prev };
      delete newStates[fileName];
      return newStates;
    });
  };

  // Save file panel (switch to summary mode)
  const handleSaveFilePanel = (fileName) => {
    setFilePanelStates(prev => ({
      ...prev,
      [fileName]: 'summary'
    }));
  };

  // Edit file panel (switch to edit mode)
  const handleEditFilePanel = (fileName) => {
    setFilePanelStates(prev => ({
      ...prev,
      [fileName]: 'edit'
    }));
  };

  // Get configured sections for a file (for summary view)
  const getConfiguredSections = (fileName) => {
    const fileData = fileSections[fileName];
    if (!fileData) return [];

    const configuredSections = [];
    Object.keys(fileData.sections).forEach(section => {
      if (fileData.sections[section] && fileData.pageRanges[section] && fileData.pageRanges[section].trim()) {
        configuredSections.push({
          name: section,
          pageRange: fileData.pageRanges[section].trim()
        });
      }
    });

    return configuredSections;
  };

  // Calculate completion status
  const getCompletionStatus = () => {
    const allSelectedSections = new Set();
    
    // Add sections from file uploads
    Object.values(fileSections).forEach(fileData => {
      Object.keys(fileData.sections).forEach(section => {
        // Only count as completed if section is checked AND page range is filled
        if (fileData.sections[section] && fileData.pageRanges[section] && fileData.pageRanges[section].trim()) {
          allSelectedSections.add(section);
        }
      });
    });
    
    // Add sections that are toggled on for AI evaluation
    Object.keys(aiEvaluationToggles).forEach(section => {
      if (aiEvaluationToggles[section]) {
        // For Student Surveys section, also check if surveys are selected
        if (section === 'Student Surveys') {
          if (selectedSurveys.length > 0) {
            allSelectedSections.add(section);
          }
        } else {
          allSelectedSections.add(section);
        }
      }
    });
    
    const completedSections = Array.from(allSelectedSections);
    const missingSections = programSections.filter(section => !completedSections.includes(section));
    
    return {
      completed: completedSections.length,
      total: programSections.length,
      missing: missingSections
    };
  };

  // Format data for PDF report generation
  const formatPdfReportPayload = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Debug logging
    console.log('formatPdfReportPayload - selectedTemplate:', selectedTemplate);
    console.log('formatPdfReportPayload - selectedTemplate?.id:', selectedTemplate?.id);
    console.log('formatPdfReportPayload - templateLoadAttempted:', templateLoadAttempted);
    
    // Try to get template ID from multiple sources
    let templateId = null;
    
    // First, try from context
    if (selectedTemplate?.id) {
      templateId = selectedTemplate.id;
      console.log('formatPdfReportPayload - Using template ID from context:', templateId);
    } else {
      // Try to load from localStorage as a last resort
      const storedTemplateId = localStorage.getItem('report_generation_template_id');
      if (storedTemplateId) {
        templateId = parseInt(storedTemplateId);
        console.log('formatPdfReportPayload - Using template ID from localStorage:', templateId);
      }
    }
    
    // Final fallback
    if (!templateId) {
      templateId = 1;
      console.log('formatPdfReportPayload - Using fallback template ID:', templateId);
    }
    
    // Create sections_data array based on programSections
    const sections_data = programSections.map(sectionName => {
      const sectionFiles = [];
      const sectionPageRanges = [];
      
      // Check if this section is toggled for AI evaluation
      const isAiEvaluation = aiEvaluationToggles[sectionName] || false;
      
      // Find all files that are assigned to this section
      programInfoFiles.forEach(file => {
        const fileData = fileSections[file.name];
        if (fileData && fileData.sections[sectionName] && fileData.pageRanges[sectionName] && fileData.pageRanges[sectionName].trim()) {
          sectionFiles.push(file.name);
          sectionPageRanges.push(fileData.pageRanges[sectionName].trim());
        }
      });
      
      return {
        name: sectionName,
        pageRanges: sectionPageRanges,
        files: sectionFiles,
        aiEvaluation: isAiEvaluation,
        selectedSurvey: sectionName === 'Student Surveys' && isAiEvaluation ? selectedSurveys : null,
        filename: sectionName === 'Student Surveys' && isAiEvaluation && selectedSurveys.length > 0 ? selectedSurveys : null
      };
    }).filter(section => section.files.length > 0 || section.aiEvaluation); // Include sections with files OR AI evaluation
    
    const payload = {
      template_id: templateId,
      program_name: programData.name || 'New Program',
      school_name: school,
      sections_data: sections_data
    };
    
    console.log('formatPdfReportPayload - final payload:', payload);
    
    return payload;
  };

  // Handle generate report
  const handleGenerateReport = async () => {
    try {
      const payload = formatPdfReportPayload();
      
      // Check if all sections are properly covered
      const status = getCompletionStatus();
      if (status.completed < status.total) {
        alert(`Please complete all sections before generating the report. Currently ${status.completed}/${status.total} sections are covered. Missing: ${status.missing.join(', ')}`);
        return;
      }
      
      // Start the progress modal
      setIsGeneratingReport(true);
      setReportProgress(0);
      setEstimatedTimeRemaining('3 min 0 s');
      
      // Record the start time
      const startTime = Date.now();
      setProgressStartTime(startTime);
      
      // Create AbortController for cancellation
      const abortController = new AbortController();
      abortControllerRef.current = abortController; // Store for cancel function
      
      // Start a simple countdown timer
      const countdownInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = Math.max(180 - elapsed, 0); // 3 minutes = 180 seconds
        const minutes = Math.floor(remaining / 60);
        const seconds = remaining % 60;
        
        // Update progress every second
        const progressPercent = Math.min((elapsed / 180) * 99, 99); // Max 99% until completion
        setReportProgress(progressPercent);
        
        // Update time remaining every 5 seconds
        if (elapsed % 5 === 0) {
          setEstimatedTimeRemaining(`${minutes} min ${seconds} s`);
        }
      }, 1000);
      
      try {
        // Generate the PDF report with polling and abort signal
        const result = await generatePdfReport(payload, programInfoFiles, (progressData) => {
          // Only handle completion status, let the countdown handle progress
          if (progressData.status === 'completed') {
            clearInterval(countdownInterval);
            setReportProgress(100);
            setEstimatedTimeRemaining('0 min 0 s');
          }
        }, abortController.signal);
        
        console.log('PDF generation result:', result);
        
        // Clear the countdown interval
        clearInterval(countdownInterval);
        
        // Clear the abort controller
        abortControllerRef.current = null;
        
        // Complete the progress
        setReportProgress(100);
        setEstimatedTimeRemaining('0 min 0 s');
        
        // Store the generated report files and move to Step 3
        setTimeout(() => {
          console.log('Setting generated report files:', result.files);
          setGeneratedReportFiles(result.files);
          
          // Set PDF conversion error if exists
          if (result.pdfError) {
            setPdfConversionError(result.pdfError);
            console.warn('PDF conversion error:', result.pdfError);
          }
          
          // Set default file type based on availability
          if (result.files.pdf) {
            setSelectedFileType('PDF');
          } else if (result.files.word) {
            setSelectedFileType('Word Doc');
          }
          
          setIsGeneratingReport(false);
          setProgressStartTime(null);
          setCurrentStep(3);
          setHasGeneratedReport(true); // Mark that a report has been generated
        }, 1000);
        
      } catch (apiError) {
        console.error('API call failed:', apiError);
        
        // Clear the countdown interval
        clearInterval(countdownInterval);
        
        // Handle specific error types with better user feedback
        if (apiError.message.includes('timed out after 5 minutes')) {
          alert('PDF generation timed out after 5 minutes. The server is processing your request but it\'s taking longer than expected. Please try again in a few minutes.');
        } else if (apiError.message.includes('502') || apiError.message.includes('Server temporarily unavailable')) {
          alert('Server is temporarily unavailable. Please try again in a few minutes.');
        } else if (apiError.message.includes('timeout')) {
          alert('Request timed out. Please check your connection and try again.');
        } else if (apiError.message.includes('cancelled')) {
          // User cancelled - don't show alert, just reset state
          console.log('PDF generation was cancelled by user');
          setIsGeneratingReport(false);
          setProgressStartTime(null);
          setReportProgress(0);
          setEstimatedTimeRemaining('3 min 0 s');
          return; // Exit early, don't show error alert
        } else {
          // For other errors, create a demo PDF for testing
          console.log('Creating demo PDF due to API failure');
          const demoContent = `%PDF-1.4
1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj
2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj
3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Contents 4 0 R>>endobj
4 0 obj<</Length 44>>stream
BT /F1 12 Tf 100 700 Td (Generated Report - Demo) Tj ET
endstream endobj
xref
0 5
0000000000 65535 f 
0000000009 00000 n 
0000000058 00000 n 
0000000115 00000 n 
0000000204 00000 n 
trailer<</Size 5/Root 1 0 R>>
startxref
297
%%EOF`;
          
          const demoBlob = new Blob([demoContent], { type: 'application/pdf' });
          const demoUrl = window.URL.createObjectURL(demoBlob);
          
          // Complete the progress
          setReportProgress(100);
          setEstimatedTimeRemaining('0 min 0 s');
          
          // Store the demo report files and move to Step 2
          setTimeout(() => {
            console.log('Setting demo report URL:', demoUrl);
            const demoFiles = {
              pdf: {
                blobUrl: demoUrl,
                blob: demoBlob,
                filename: 'demo_report.pdf',
                type: 'application/pdf'
              }
            };
            setGeneratedReportFiles(demoFiles);
            setSelectedFileType('PDF');
            setIsGeneratingReport(false);
            setProgressStartTime(null);
            setCurrentStep(2);
            setHasGeneratedReport(true); // Mark that a report has been generated
          }, 1000);
        }
      }
      
    } catch (error) {
      console.error('Error in handleGenerateReport:', error);
      setIsGeneratingReport(false);
      setProgressStartTime(null);
      alert(`Error generating report: ${error.message}`);
    }
  };
  
  // Handle cancel report generation
  const handleCancelReportGeneration = () => {
    // Abort the current PDF generation process
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setIsGeneratingReport(false);
    setReportProgress(0);
    setEstimatedTimeRemaining('3 min 0 s');
    setProgressStartTime(null);
  };

  // Handle export download
  const handleExportDownload = async () => {
    // Get the current file based on selected type
    const currentFile = selectedFileType === 'PDF' ? generatedReportFiles.pdf : generatedReportFiles.word;
    
    if (!currentFile) {
      alert(`No ${selectedFileType} file available for download`);
      return;
    }

    try {
      let downloadUrl;
      
      // If we have a blob (from base64), use it directly
      if (currentFile.blob) {
        downloadUrl = window.URL.createObjectURL(currentFile.blob);
      } else {
        // If we have a URL (from S3), fetch it first to create a blob
        console.log('Fetching file from URL for download:', currentFile.blobUrl);
        const response = await fetch(currentFile.blobUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        const blob = await response.blob();
        downloadUrl = window.URL.createObjectURL(blob);
      }
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = currentFile.filename;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  // Handle finish
  const handleFinish = () => {
    // Get school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Preserve application state for the AssuranceReport (same as handleBack)
    const preservedState = {
      ...location.state,
      returnedFromReportGeneration: true,
      selectedTemplate: selectedTemplate,
      programData: programData,
      templateId: selectedTemplate?.id,
      courseList: location.state?.courseList || [], // Preserve courseList
      // Preserve any other relevant state
      tab: 'Curriculum Mapping' // Default tab to return to
    };
    
    // Add generated report data if available
    if (hasGeneratedReport && generatedReportFiles) {
      preservedState.generatedReportData = {
        files: generatedReportFiles,
        programName: programData.name,
        templateId: selectedTemplate?.id,
        generatedAt: new Date().toISOString(),
        status: 'completed'
      };
    }
    
    // Also store state in sessionStorage as backup
    const backupState = {
      selectedTemplate: selectedTemplate,
      programData: programData,
      courseList: location.state?.courseList || [], // Preserve courseList in backup
      returnedFromReportGeneration: true,
      generatedReportData: hasGeneratedReport ? {
        files: generatedReportFiles,
        programName: programData.name,
        templateId: selectedTemplate?.id,
        generatedAt: new Date().toISOString(),
        status: 'completed'
      } : null,
      timestamp: Date.now()
    };
    sessionStorage.setItem('report_generation_return_state', JSON.stringify(backupState));
    
    // Navigate back to the AssuranceReport or Evidence Library
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, {
        state: preservedState
      });
    } else {
      navigate(`/assessment?school=${school}`, {
        state: preservedState
      });
    }
  };

  // Handle going back to the generated report
  const handleNextToReport = () => {
    setCurrentStep(3);
  };

  // Add this function to render each file panel based on its state
  const renderFilePanel = (file, section) => {
    if (filePanelStates[file.name] === 'summary') {
      // Render summary view for this file
      const configuredSections = getConfiguredSections(file.name);
      return (
        <div className="file-panel-summary">
          <div className="summary-header">
            <div>
              <div className="file-name">{file.name}</div>
              <div className="file-size">{(file.size / (1024 * 1024)).toFixed(1)} MB</div>
            </div>
          </div>
          <div className="selected-sections-header">
            <span className="material-symbols-outlined" style={{ color: '#6366f1', fontSize: 20, verticalAlign: 'middle' }}>check_circle</span>
            <span className="sections-configured-link" style={{ color: '#6366f1', fontWeight: 500, marginLeft: 4 }}>
              {configuredSections.length} sections configured
            </span>
          </div>
          <div className="configured-sections">
            {configuredSections.map((section, idx) => (
              <div className="configured-section" key={idx}>
                <span className="section-name">{section.name}</span>
                <span className="section-pages">Pages: {section.pageRange}</span>
              </div>
            ))}
          </div>
          <div className="summary-actions">
            <button className="edit-btn" onClick={() => handleEditFilePanel(file.name)}>
              <span className="material-symbols-outlined">edit_square</span> Edit
            </button>
          </div>
        </div>
      );
    } else {
      // Render edit view for this file (existing code)
      return (
        <div key={file.name} className="file-analysis-panel">
          <div className="file-panel-header">
            <div className="file-info">
              <span className="file-name">{file.name}</span>
            </div>
          </div>
          
          {/* Toggle for specific sections */}
          {programSections.includes(section) && (
            <div className="section-panel-toggle-container">
              <div 
                className={`section-panel-toggle ${aiEvaluationToggles[section] ? 'active' : ''}`}
                onClick={() => handleAiEvaluationToggle(section)}
              >
                <div className="section-panel-toggle-slider"></div>
              </div>
              <span className="section-panel-toggle-label">
                Apply AristAI Evaluation Results
              </span>
            </div>
          )}
          
          <div className="file-panel-content">
            {/* Always show file upload interface */}
            <h4>Select documents that contain this section:</h4>
            <div className="sections-grid">
              {programInfoFiles.map((file, fileIndex) => (
                <div key={fileIndex} className="section-item">
                  <div className="section-checkbox">
                    <div className="section-checkbox-row">
                      <input
                        type="checkbox"
                        id={`${section}-${file.name}`}
                        checked={fileSections[file.name]?.sections[section] || false}
                        onChange={(e) => handleSectionChange(file.name, section, e.target.checked)}
                      />
                      <label htmlFor={`${section}-${file.name}`}>{file.name}</label>
                    </div>
                    {pageRangeErrors[`${file.name}-${section}`] && (
                      <div className="page-range-error">
                        <span className="material-symbols-outlined error-icon">error_outline</span>
                        <span className="error-text">Please enter page range, e.g. 1-5</span>
                      </div>
                    )}
                  </div>
                  {fileSections[file.name]?.sections[section] && (
                    <div className="page-range-container">
                      <input
                        type="text"
                        placeholder="Page Range, e.g. 1-5"
                        className={`page-range-input ${pageRangeErrors[`${file.name}-${section}`] ? 'error' : ''}`}
                        value={fileSections[file.name]?.pageRanges[section] || ''}
                        onChange={(e) => handlePageRangeChange(file.name, section, e.target.value)}
                        onFocus={() => handlePageRangeFocus(file.name, section)}
                        onBlur={() => handlePageRangeBlur(file.name, section)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="panel-footer">
              <button className="section-reset-btn" onClick={() => {
                // Clear all checkboxes and page ranges for this section in all files
                programInfoFiles.forEach(file => {
                  handleSectionChange(file.name, section, false);
                  handlePageRangeChange(file.name, section, '');
                });
                // Also turn off AristAI evaluation toggle for this section
                if (aiEvaluationToggles.hasOwnProperty(section)) {
                  setAiEvaluationToggles(prev => ({
                    ...prev,
                    [section]: false
                  }));
                }
              }}>
                Reset
              </button>
              <div className="panel-actions">
                <button className="save-btn" onClick={() => setSectionPanelStates(prev => ({ ...prev, [section]: 'summary' }))}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // Render step-specific content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1Content();
      case 2:
        return renderStep2Content();
      case 3:
        return renderStep3Content();
      default:
        return renderStep1Content();
    }
  };

  // Step 1: Upload Documents Content
  const renderStep1Content = () => (
    <>
      <div className="step-header">
        <h3>
          Upload Documents <Icon icon="maki:arrow" style={{ width: 15, height: 15, position: 'relative', top: '1px', transform: 'rotate(0deg)', opacity: 1, verticalAlign: 'middle' }} />
        </h3>
      </div>
      
      <p className="step-description">
        Upload your own documents to add to the upcoming report, or choose to add information from certain sections based on the evaluation results.
      </p>
      
      {/* Required Sections Box */}
      <div className="required-sections-box">
        <div className="required-sections-header">
          <img src="/icons8-idea.svg" alt="Idea" className="lightbulb-icon" />
          <h4>Required Sections for Your Report</h4>
        </div>
        <p className="sections-note">
          For some sections, you can also use AristAI evaluation results instead of uploading.
        </p>
        <div className="required-sections-tags">
          {programSections.map(section => (
            <span key={section} className="section-tag">{section}</span>
          ))}
        </div>
      </div>

      <h5 className="upload-instruction">Please upload all files that constitute your report</h5>
      
      {/* Upload Interface */}
      <div className="program-info-upload-section">
        {/* Always show upload area */}
        <div 
          className={`program-info-upload-area ${isDragOver ? 'drag-over' : ''}`}
          onDragEnter={handleProgramInfoDragEnter}
          onDragLeave={handleProgramInfoDragLeave}
          onDragOver={handleProgramInfoDragOver}
          onDrop={handleProgramInfoDrop}
          onClick={handleProgramInfoUploadAreaClick}
        >
          <p className="drag-text">Drag and drop files here</p>
          <p className="or-text">or</p>
          <button type="button" className="browse-button">Browse</button>
          <p className="upload-format">Supported Formats: PDF | Maximum 10 files</p>
        </div>
        
        <input
          id="program-info-file-input"
          type="file"
          multiple
          accept=".pdf"
          onChange={handleProgramInfoFileInput}
          style={{ display: 'none' }}
        />
        
        {/* Upload Progress */}
        {programInfoFiles.length > 0 && uploadProgress > 0 && uploadProgress < 100 && (
          <div className="upload-progress">
            <div className="upload-status">
              Uploading {programInfoFiles.length} file{programInfoFiles.length > 1 ? 's' : ''}
            </div>
            <div className="progress-details">
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill"
                  style={{ width: `${uploadProgress}%` }} 
                />
              </div>
              <div className="file-size">
                {(totalUploadSize / (1024 * 1024)).toFixed(1)} MB - {uploadProgress}%
              </div>
            </div>
          </div>
        )}
        
        {/* PDF Preview Cards - Show below upload area */}
        {programInfoFiles.length > 0 && (
          <div className="pdf-preview-cards">
            {programInfoFiles.map((file, index) => (
              <div key={index} className="pdf-preview-card">
                <div className="pdf-preview-image">
                  {pdfPreviews[file.name] ? (
                    <img 
                      src={pdfPreviews[file.name]} 
                      alt={`Preview of ${file.name}`}
                      className="pdf-thumbnail"
                    />
                  ) : (
                    <div className="pdf-placeholder">
                      <span className="pdf-icon">📄</span>
                      <span className="loading-text">Loading preview...</span>
                    </div>
                  )}
                </div>
                <div className="pdf-file-info">
                  <div className="pdf-file-name">{file.name}</div>
                  <div className="pdf-file-size">{(file.size / (1024 * 1024)).toFixed(1)} MB</div>
                </div>
                <button 
                  className="pdf-delete-btn"
                  onClick={() => handleRemoveFile(file.name)}
                  title="Delete file"
                >
                  <span className="material-symbols-outlined">delete_outline</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // Step 2: Organize Files By Section
  const renderStep2Content = () => (
    <>
      <div className="step-header">
        <h3>Organize Files By Section <Icon icon="maki:arrow" style={{ width: 15, height: 15, position: 'relative', top: '1px', transform: 'rotate(0deg)', opacity: 1, verticalAlign: 'middle' }} /></h3>
      </div>
      
      <div className="content-header">
        <p>Please select the documents that contain each section, and choose whether to include AristAI evaluation results.</p>
      </div>

      {/* File Analysis Interface */}
      <div className="program-info-upload-section">
        {programInfoFiles.length > 0 && (
          <>
            {/* Status Header */}
            <div className="file-status-container">
              {(() => {
                const status = getCompletionStatus();
                const isComplete = status.completed === status.total;
                return (
                  <div className="completion-status-box">
                    <div className="completion-progress">
                      <div className="progress-number">{status.completed} / {status.total}</div>
                      <div className="section-progress-bar">
                        <div 
                          className="section-progress-fill" 
                          style={{ width: `${(status.completed / status.total) * 100}%` }}
                        />
                      </div>
                      <div className="progress-label">Sections Covered</div>
                    </div>
                    
                    <div className="completion-details">
                      <div className={`completion-message ${isComplete ? 'complete' : ''}`}>
                        <strong>
                          {isComplete 
                            ? 'All sections are covered. You may now proceed to generate the report.' 
                            : 'To complete the report, please fill all sections!'}
                        </strong>
                      </div>
                      {!isComplete && status.missing.length > 0 && (
                        <div className="missing-sections">
                          <span className="missing-label">Missing Files in Sections:</span>
                          <span className="missing-list">{status.missing.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Section Analysis Panels */}
            <div className="file-analysis-panels">
              {programSections.map((section, sectionIndex) => {
                const hasToggle = section === 'Student Surveys' || section === 'Learning Outcomes Assessment';
                const isAiEvaluation = hasToggle && aiEvaluationToggles[section];

                // Render summary or edit mode for this section
                if (sectionPanelStates[section] === 'summary') {
                  // Render summary view for this section
                  // Gather all files that have this section checked and a page range
                  const configuredFiles = programInfoFiles.filter(file => fileSections[file.name]?.sections[section] && fileSections[file.name]?.pageRanges[section]);
                  return (
                    <div key={sectionIndex} className="file-analysis-panel">
                      <div className="file-panel-header">
                        <div className="file-info">
                          <span className="file-name">{section}</span>
                        </div>
                      </div>
                      <div className="file-panel-content">
                        <div className="summary-header">
                          <h4>Selected Files:</h4>
                          <button className="edit-btn" onClick={() => setSectionPanelStates(prev => ({ ...prev, [section]: 'edit' }))}>
                            <Icon icon="mingcute:edit-line" style={{ fontSize: 20, verticalAlign: 'middle' }} /> Edit
                          </button>
                        </div>
                        <div className="configured-sections">
                          {configuredFiles.length === 0 ? (
                            <div style={{ color: '#888', fontStyle: 'normal', marginBottom: 12, fontSize: '14px' }}>No files selected.</div>
                          ) : (
                            configuredFiles.map((file, idx) => (
                              <div className="configured-section" key={idx}>
                                <span className="section-name">{file.name}</span>
                                <span className="section-pages">Pages: {fileSections[file.name]?.pageRanges[section]}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Edit mode (default)
                return (
                  <div key={sectionIndex} className="file-analysis-panel">
                    <div className="file-panel-header">
                      <div className="file-info">
                        <span className="file-name">{section}</span>
                      </div>
                    </div>

                    {/* Toggle for specific sections */}
                    {hasToggle && (
                      <div className="section-panel-toggle-container">
                        <div
                          className={`section-panel-toggle ${isAiEvaluation ? 'active' : ''}`}
                          onClick={() => handleAiEvaluationToggle(section)}
                        >
                          <div className="section-panel-toggle-slider"></div>
                        </div>
                        <span className="section-panel-toggle-label">
                          Apply AristAI Evaluation Results
                        </span>
                      </div>
                    )}

                    {/* Survey dropdown for Student Survey section */}
                    {section === 'Student Surveys' && isAiEvaluation && (
                      <div className="survey-dropdown-container">
                        <label className="survey-dropdown-label">Select surveys to incorporate:</label>
                        <div className="survey-dropdown-wrapper">
                          <select
                            value=""
                            onChange={(e) => handleSurveySelect(e.target.value)}
                            className={`survey-dropdown-select ${selectedSurveys.length === 0 && aiEvaluationToggles['Student Surveys'] ? 'error' : ''}`}
                            disabled={surveysLoading}
                          >
                            <option value="">Please select a survey</option>
                            {availableSurveys
                              .filter(surveyName => !selectedSurveys.includes(surveyName))
                              .map((surveyName, index) => (
                                <option key={index} value={surveyName}>
                                  {surveyName}
                                </option>
                              ))}
                          </select>
                          <div className="survey-dropdown-arrow">
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                        
                        {/* Selected surveys display */}
                        {selectedSurveys.length > 0 && (
                          <div className="selected-surveys-container">
                            <div className="selected-surveys-label">Selected surveys:</div>
                            <div className="selected-surveys-list">
                              {selectedSurveys.map((surveyName, index) => (
                                <div key={index} className="selected-survey-tag">
                                  <span className="survey-tag-name">{surveyName}</span>
                                  <button
                                    type="button"
                                    className="survey-tag-remove"
                                    onClick={() => handleRemoveSurvey(surveyName)}
                                    title="Remove survey"
                                  >
                                    <span className="material-symbols-outlined">close</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {surveysLoading && (
                          <div className="survey-loading">Loading surveys...</div>
                        )}
                        {selectedSurveys.length === 0 && aiEvaluationToggles['Student Surveys'] && (
                          <div className="survey-error">
                            <span className="material-symbols-outlined error-icon">error_outline</span>
                            <span className="error-text">Please select at least one survey to continue</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="file-panel-content">
                      <h4>Select documents that contain this section:</h4>
                      <div className="sections-grid">
                        {programInfoFiles.map((file, fileIndex) => (
                          <div key={fileIndex} className="section-item">
                            <div className="section-checkbox">
                              <div className="section-checkbox-row">
                                <input
                                  type="checkbox"
                                  id={`${section}-${file.name}`}
                                  checked={fileSections[file.name]?.sections[section] || false}
                                  onChange={(e) => handleSectionChange(file.name, section, e.target.checked)}
                                />
                                <label htmlFor={`${section}-${file.name}`}>{file.name}</label>
                              </div>
                              {pageRangeErrors[`${file.name}-${section}`] && (
                                <div className="page-range-error">
                                  <span className="material-symbols-outlined error-icon">error_outline</span>
                                  <span className="error-text">Please enter page range, e.g. 1-5</span>
                                </div>
                              )}
                            </div>
                            {fileSections[file.name]?.sections[section] && (
                              <div className="page-range-container">
                                <input
                                  type="text"
                                  placeholder="Page Range, e.g. 1-5"
                                  className={`page-range-input ${pageRangeErrors[`${file.name}-${section}`] ? 'error' : ''}`}
                                  value={fileSections[file.name]?.pageRanges[section] || ''}
                                  onChange={(e) => handlePageRangeChange(file.name, section, e.target.value)}
                                  onFocus={() => handlePageRangeFocus(file.name, section)}
                                  onBlur={() => handlePageRangeBlur(file.name, section)}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="panel-footer">
                        <button className="section-reset-btn" onClick={() => {
                          // Clear all checkboxes and page ranges for this section in all files
                          programInfoFiles.forEach(file => {
                            handleSectionChange(file.name, section, false);
                            handlePageRangeChange(file.name, section, '');
                          });
                          // Also turn off AristAI evaluation toggle for this section
                          if (aiEvaluationToggles.hasOwnProperty(section)) {
                            setAiEvaluationToggles(prev => ({
                              ...prev,
                              [section]: false
                            }));
                          }
                        }}>
                          Reset
                        </button>
                        <div className="panel-actions">
                          <button className="save-btn" onClick={() => setSectionPanelStates(prev => ({ ...prev, [section]: 'summary' }))}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );

  // Step 3: Review & Download Content (previously Step 2)
  const renderStep3Content = () => {
    console.log('Rendering Step 3, generatedReportFiles:', generatedReportFiles);
    
    return (
      <>
        <div className="step-header">
          <h3>Review & Download<Icon icon="maki:arrow" style={{ width: 15, height: 15, position: 'relative', top: '1px', transform: 'rotate(0deg)', opacity: 1, verticalAlign: 'middle' }} /></h3>
        </div>
        
        <div className="content-header">
          <p>Preview the program report you've generated.</p>
        </div>

        <div className="review-download-container">
          {/* Document Preview */}
          <div className="document-preview">
            <div className="preview-header">
              <span className="page-indicator">1/12</span>
            </div>
            <div className="preview-content">
              {(() => {
                const currentFile = selectedFileType === 'PDF' ? generatedReportFiles.pdf : generatedReportFiles.word;
                
                if (!currentFile) {
                  return (
                    <div className="preview-placeholder">
                      <p>Loading document preview...</p>
                      <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                        Debug: No {selectedFileType} file available
                      </p>
                    </div>
                  );
                }
                
                // Handle PDF preview
                if (selectedFileType === 'PDF' && currentFile.type === 'application/pdf') {
                  return (
                    <div className="pdf-preview-container">
                      {!pdfPreviewError ? (
                        <iframe
                          key={`pdf-preview-${selectedFileType}-${currentFile.filename}`}
                          src={`${currentFile.blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                          title="Generated Report Preview"
                          className="pdf-preview"
                          type="application/pdf"
                          frameBorder="0"
                          onError={() => {
                            console.log('PDF preview failed to load');
                            setPdfPreviewError(true);
                          }}
                          onLoad={() => {
                            console.log('PDF preview loaded successfully');
                            setPdfPreviewError(false);
                          }}
                        />
                      ) : (
                        <div className="preview-error">
                          <div className="error-icon">📄</div>
                          <h4>Preview not available</h4>
                          <p>This browser doesn't support PDF preview.</p>
                          <a 
                            href={currentFile.blobUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="download-link"
                          >
                            Download PDF to view
                          </a>
                        </div>
                      )}
                      <div className="pdf-fallback">
                        <p>Can't see the preview? 
                          <a 
                            href={currentFile.blobUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="preview-link"
                          >
                            Click here to view the PDF
                          </a>
                        </p>
                      </div>
                    </div>
                  );
                }
                
                // Handle Word document preview
                if (selectedFileType === 'Word Doc' && currentFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                  return (
                    <div className="word-preview-container">
                      <div className="preview-error">
                        <div className="error-icon">📝</div>
                        <h4>Word Document Preview</h4>
                        <p>Word documents cannot be previewed in the browser.</p>
                        <p>Click below to download and view the document.</p>
                        <a 
                          href={currentFile.blobUrl} 
                          download={currentFile.filename}
                          className="download-link"
                        >
                          Download Word Document
                        </a>
                      </div>
                      <div className="word-preview-info">
                        <p><strong>Filename:</strong> {currentFile.filename}</p>
                        <p><strong>Type:</strong> Microsoft Word Document</p>
                        <p><strong>Size:</strong> {(currentFile.blob?.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className="preview-placeholder">
                    <p>Unsupported file type for preview</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Download Options */}
          <div className="download-options">
            <h3>Download Options</h3>
            
            <div className="file-type-section">
              <label className="section-label">File Type</label>
              <div className="file-type-dropdown">
                <select 
                  value={selectedFileType} 
                  onChange={(e) => setSelectedFileType(e.target.value)}
                  className="file-type-select"
                >
                  {generatedReportFiles.pdf && (
                    <option value="PDF">📄 PDF</option>
                  )}
                  {generatedReportFiles.word && (
                    <option value="Word Doc">📝 Word Doc</option>
                  )}
                </select>
                <div className="dropdown-arrow">
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              
              {/* PDF Conversion Error Warning */}
              {pdfConversionError && !generatedReportFiles.pdf && (
                <div className="pdf-conversion-error">
                  <div className="error-icon">⚠️</div>
                  <div className="error-message">
                    <strong>PDF conversion failed:</strong>
                    <p>{pdfConversionError}</p>
                  </div>
                </div>
              )}
            </div>

            <button 
              className="export-download-btn"
              onClick={handleExportDownload}
              disabled={!generatedReportFiles.pdf && !generatedReportFiles.word}
            >
              Export
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="report-generation">
      {/* Header with Progression Bar and Back Button */}
      <div className="page-header">
        <div className="progression-bar">
          {progressionSteps.map((step, index) => (
            <div key={step.number} className="progression-step">
              <div className={`step-circle ${step.active ? 'active' : step.completed ? 'completed' : 'inactive'}`}>
                {step.completed ? step.number : step.number}
              </div>
              <span className={`step-title ${step.active ? 'active' : step.completed ? 'completed' : 'inactive'}`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>
        <button className="back-button-1" onClick={handleBack}>
          <span className="back-icon">←</span>
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Render step-specific content */}
        {renderStepContent()}
      </div>

      {/* Footer Navigation */}
      <div className="footer-navigation">
        <button className="back-btn" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
          Back
        </button>
        
        <div className="footer-actions">
          {currentStep === 3 ? (
            <button 
              className="finish-btn" 
              onClick={handleFinish}
            >
              Finish
            </button>
          ) : currentStep === 2 ? (
            hasGeneratedReport ? (
              <div className="footer-buttons-group">
                <button 
                  className="next-btn" 
                  onClick={handleNextToReport}
                >
                  Next
                </button>
                <button 
                  className="generate-new-report-btn"
                  onClick={handleGenerateReport}
                >
                  Generate New Report
                </button>
              </div>
            ) : (
              <button 
                className="generate-report-btn" 
                onClick={handleGenerateReport}
                disabled={(() => {
                  if (programInfoFiles.length === 0) return true;
                  const status = getCompletionStatus();
                  return status.completed < status.total;
                })()}
              >
                Generate Report
              </button>
            )
          ) : (
            <button 
              className="next-btn" 
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={programInfoFiles.length === 0}
            >
              Next
            </button>
          )}
        </div>
      </div>

      {/* Report Generation Progress Modal */}
      {isGeneratingReport && (
        <div className="report-generation-modal">
          <div className="report-generation-content">
            <div className="progress-header">
              <h2>Generating Report</h2>
              <span className="progress-percentage">{Math.round(reportProgress)}%</span>
            </div>
            
            <div className="progress-bar-container">
              <div className="progress-bar-track">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${reportProgress}%` }}
                />
              </div>
            </div>
            
            <div className="progress-info">
              <p className="estimated-time">
                Estimated time remaining: <span className="time-value">{estimatedTimeRemaining}</span>
              </p>
              
              <div className="progress-message">
                <div className="info-icon">ℹ️</div>
                <p>This generation usually takes <strong>3 minutes</strong> to complete.</p>
              </div>
            </div>
            
            <div className="progress-actions">
              <button className="cancel-generation-btn" onClick={handleCancelReportGeneration}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportGeneration; 